import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Upload, Send, Mail, Eye, RotateCcw, Loader2, 
  Check, X, AlertCircle, Users, History, RefreshCw, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import AgreementGeneratorTab from '../components/proposal-mailer/AgreementGeneratorTab';
import { ClientPicker, BulkClientAdder } from '../components/proposal-mailer/ClientAutofill';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default email template
const DEFAULT_BODY_TEMPLATE = `Hello <name>,

Greetings from Daruwalla Consultancy Services LLP.

This is <sender_name> from Daruwalla Consultancy Services. We would be pleased to collaborate and support your organization with our recruitment and staffing services.

A brief introduction: Daruwalla Consultancy Services is a recruitment and staffing firm specializing in both IT and Non-IT hiring with Pan India reach. We have experience working across multiple industries and focus on delivering pre-screened, vetted, and quality candidates as per client requirements within defined timelines.

I am sharing our agreement with you for your reference. Kindly share your company name, CIN number, registered address and name of the authorized signatory so we can make the agreement.

Request you to also share the following details to help us understand your requirements better:
• Job Description (JD)
• Company Profile
• Budget / CTC Range
• Location & Number of Positions
• Any specific hiring requirements

Commercial Terms:
• Our professional fees will be [fee_percent]% of the selected candidate's annual CTC.
• We provide a replacement guarantee of [replacement_months] months.
• Payment is requested within [payment_days] days from the candidate's date of joining.
• As per the agreed commercial terms, a minimum of [min_candidates] candidates are required to be hired within a period of [hire_days] days. The replacement guarantee period will be applicable only upon successful onboarding of all [min_candidates] candidates through our consultancy.

Based on the shared details, we will start sourcing and sharing suitable pre-screened candidates accordingly.

We look forward to building a strong and successful partnership with your organization.

Thanks & Regards,
<sender_name>
<sender_email>`;

// Setup file slot labels
const SLOT_LABELS = {
  company_portfolio: 'Company Portfolio',
  rate_fee_card: 'Rate Fee Card',
  dummy_agreement: 'Dummy Agreement'
};

export default function ProposalMailerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Setup files
  const [setupFiles, setSetupFiles] = useState([]);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  
  // Compose state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  
  // Commercial terms state
  const [terms, setTerms] = useState({
    feePercent: { enabled: true, value: '8.33' },
    replacementMonths: { enabled: true, value: '5' },
    paymentDays: { enabled: true, value: '7' },
    minCandidates: { enabled: true, value: '3' },
    hireDays: { enabled: true, value: '30' }
  });
  
  // Review state
  const [toEmail, setToEmail] = useState('');
  const [ccEmail] = useState('care@daruwallas.com');
  const [subject, setSubject] = useState('Recruitment Services Proposal | Daruwalla Consultancy Services LLP');
  const [bodyText, setBodyText] = useState('');
  const [previewTab, setPreviewTab] = useState('edit');
  
  // Bulk send state
  const [bulkRecipients, setBulkRecipients] = useState('');
  
  // UI state
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  
  // Get sender name from logged-in user
  // Sender identity comes from the server, never from the browser.
  // `useAuth()` hydrates from the `ats_user` blob written to localStorage at
  // login, and that blob is never refreshed. A browser that once signed in as
  // the seeded admin account (admin@ats.com / "System Admin", server.py:25045)
  // therefore keeps that name in this composer for ever - which is why
  // proposals signed off "System Admin" even when the sender was Ruhshad.
  // /api/sender-identity/me resolves the person from the bearer token instead.
  const [identity, setIdentity] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('ats_token');
        const res = await fetch(`${API_URL}/api/sender-identity/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setIdentity(data);
      } catch (e) {
        // Keep whatever the cache held rather than blocking the screen.
      }
    })();
    return () => { alive = false; };
  }, []);
  // full_name before name: server.py:35746 and the old line here both read
  // `name` first, so a single legacy `name` field would reintroduce the bug.
  const senderName = (identity && identity.full_name) || user?.full_name || user?.name || 'Team Member';
  const senderEmail = (identity && identity.from_email) || 'hr@daruwallas.com';
  
  // Load setup files
  const loadSetupFiles = useCallback(async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/setup-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSetupFiles(data.files || []);
      }
    } catch (e) {
      console.error('Error loading setup files:', e);
    }
  }, []);
  
  useEffect(() => {
    loadSetupFiles();
  }, [loadSetupFiles]);
  
  // Sync toEmail with clientEmail
  useEffect(() => {
    setToEmail(clientEmail);
  }, [clientEmail]);
  
  const updateBodyText = useCallback(() => {
    let text = DEFAULT_BODY_TEMPLATE;
    
    // Replace placeholders with current values
    text = text.replace('<name>', clientName || '<name>');
    text = text.replace(/<sender_name>/g, senderName);
    text = text.replace(/<sender_email>/g, senderEmail);
    text = text.replace('[fee_percent]', terms.feePercent.value);
    text = text.replace('[replacement_months]', terms.replacementMonths.value);
    text = text.replace('[payment_days]', terms.paymentDays.value);
    text = text.replace(/\[min_candidates\]/g, terms.minCandidates.value);
    text = text.replace('[hire_days]', terms.hireDays.value);
    
    // Check if any commercial term is enabled
    const anyTermEnabled = terms.feePercent.enabled || 
                           terms.replacementMonths.enabled || 
                           terms.paymentDays.enabled || 
                           terms.minCandidates.enabled;
    
    // Remove disabled term lines
    const lines = text.split('\n');
    const filteredLines = lines.filter(line => {
      // Remove "Commercial Terms:" header if no terms are enabled
      if (!anyTermEnabled && line.trim() === 'Commercial Terms:') return false;
      if (!terms.feePercent.enabled && line.includes('professional fees will be')) return false;
      if (!terms.replacementMonths.enabled && line.includes('replacement guarantee of')) return false;
      if (!terms.paymentDays.enabled && line.includes('Payment is requested within')) return false;
      if (!terms.minCandidates.enabled && line.includes('minimum of') && line.includes('candidates are required')) return false;
      return true;
    });
    
    setBodyText(filteredLines.join('\n'));
  }, [terms, clientName, senderName, senderEmail]);
  
  // Initialize body text when terms or client name changes
  useEffect(() => {
    updateBodyText();
  }, [updateBodyText]);
  
  const handleReset = () => {
    updateBodyText();
    toast.success('Template reset to default');
  };
  
  // Handle setup file upload
  const handleFileUpload = async (slot, file) => {
    if (!file) return;
    
    setUploadingSlot(slot);
    try {
      const token = localStorage.getItem('ats_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/proposal-mailer/setup-files/${slot}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`${SLOT_LABELS[slot]} uploaded successfully`);
        loadSetupFiles();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed');
    }
    setUploadingSlot(null);
  };
  
  // Send test email
  const handleSendTest = async () => {
    if (!bodyText.trim()) {
      toast.error('Email body is empty');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_name: clientName || 'Test Client',
          client_email: user?.email || '',
          to_email: user?.email || '',
          cc_email: '',
          subject: subject,
          body_text: bodyText,
          is_test: true
        })
      });
      
      if (res.ok) {
        toast.success(`Test email sent to ${user?.email}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send test email');
      }
    } catch (e) {
      toast.error('Failed to send test email');
    }
    setSendingTest(false);
  };
  
  // Send actual email
  const handleSend = async () => {
    if (!toEmail) {
      toast.error('Recipient email is required');
      return;
    }
    if (!bodyText.trim()) {
      toast.error('Email body is empty');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_name: clientName,
          client_email: clientEmail,
          to_email: toEmail,
          cc_email: ccEmail,
          subject: subject,
          body_text: bodyText,
          is_test: false
        })
      });
      
      if (res.ok) {
        toast.success('Proposal email sent successfully!');
        // Reset form
        setClientName('');
        setClientEmail('');
        setToEmail('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    }
    setSending(false);
  };
  
  // Bulk send
  const handleBulkSend = async () => {
    if (!bulkRecipients.trim()) {
      toast.error('No recipients entered');
      return;
    }
    
    setSendingBulk(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/proposal-mailer/bulk-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients: bulkRecipients,
          subject: subject,
          body_template: bodyText
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Sent to ${data.sent_count} recipients`);
        if (data.invalid_lines?.length > 0) {
          toast.warning(`Skipped ${data.invalid_lines.length} invalid lines`);
        }
        setBulkRecipients('');
      } else {
        toast.error(data.detail || 'Bulk send failed');
      }
    } catch (e) {
      toast.error('Bulk send failed');
    }
    setSendingBulk(false);
  };
  
  // Count bulk recipients
  const bulkCount = bulkRecipients.trim().split('\n').filter(line => {
    const parts = line.trim().split(',');
    return parts.length >= 2 && parts[1].includes('@');
  }).length;
  
  // Generate HTML preview
  const generateHtmlPreview = () => {
    let html = bodyText;
    // Simple conversion for preview
    html = html.replace(/\n/g, '<br>');
    html = html.replace(/Daruwalla Consultancy Services LLP/g, '<strong>Daruwalla Consultancy Services LLP</strong>');
    html = html.replace(/Daruwalla Consultancy Services/g, '<strong>Daruwalla Consultancy Services</strong>');
    
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="background-color: #1b5e20; padding: 20px; text-align: center; color: white; border-radius: 8px 8px 0 0;">
          <h2 style="margin: 0;">Daruwalla Consultancy Services LLP</h2>
          <p style="margin: 5px 0 0 0; font-size: 12px; color: #c8e6c9;">Recruitment & Staffing — Pan India</p>
        </div>
        <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
          ${html}
        </div>
        <div style="background-color: #fafafa; padding: 15px; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 8px 8px;">
          <p style="margin: 0; font-size: 11px; color: #757575;">
            This message and any attachments are confidential and intended solely for the addressee.
          </p>
          <p style="margin: 10px 0 0 0; font-size: 12px; color: #1b5e20; font-weight: bold;">
            Daruwalla Consultancy Services LLP
          </p>
        </div>
      </div>
    `;
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Client Documents</h1>
            <p className="text-slate-500">Send proposals and generate agreements</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/ats/proposal-mailer/deals')}
              data-testid="view-deals-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Deals
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/ats/proposal-mailer/history')}
              data-testid="view-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>
        
        {/* Module-level Tabs */}
        <Tabs defaultValue="proposal-mailer" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="proposal-mailer" className="gap-2" data-testid="proposal-mailer-tab">
              <Mail className="w-4 h-4" />
              Proposal Mailer
            </TabsTrigger>
            <TabsTrigger value="agreement-generator" className="gap-2" data-testid="agreement-generator-tab">
              <FileSignature className="w-4 h-4" />
              Agreement Generator
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="proposal-mailer">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Setup Files & Compose */}
              <div className="lg:col-span-1 space-y-6">
                {/* Setup Files */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      Setup Files
                    </CardTitle>
                    <CardDescription>
                      {isAdmin ? 'Upload PDFs to attach to all proposals' : 'Files attached to all proposals'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {['company_portfolio', 'rate_fee_card', 'dummy_agreement'].map(slot => {
                      const file = setupFiles.find(f => f.slot === slot);
                      return (
                        <div key={slot} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-slate-700">{SLOT_LABELS[slot]}</div>
                            {file?.filename ? (
                              <div className="text-xs text-slate-500 truncate">{file.filename}</div>
                            ) : (
                              <div className="text-xs text-amber-600">Not uploaded</div>
                            )}
                          </div>
                          {isAdmin && (
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                onChange={(e) => handleFileUpload(slot, e.target.files?.[0])}
                                disabled={uploadingSlot === slot}
                                data-testid={`upload-${slot}`}
                              />
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="pointer-events-none"
                                disabled={uploadingSlot === slot}
                              >
                                {uploadingSlot === slot ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                              </Button>
                            </label>
                          )}
                        </div>
                  );
                })}
              </CardContent>
            </Card>
            
            {/* Compose - Step 1 */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">1. Compose</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ClientPicker
                  onPick={(c) => {
                    if (c.name) setClientName(c.name);
                    if (c.email) { setClientEmail(c.email); setToEmail(c.email); }
                  }}
                />
                <div>
                  <Label>Client Name</Label>
                  <Input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter client name"
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label>Client Email</Label>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="client@company.com"
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <Label>Your Name</Label>
                  <Input
                    value={senderName}
                    disabled
                    className="bg-slate-100"
                    data-testid="sender-name-input"
                  />
                  <p className="text-xs text-slate-500 mt-1" data-testid="sender-email-hint">
                    {senderEmail}
                  </p>
                </div>
                
                {/* Commercial Terms */}
                <div className="pt-2">
                  <Label className="text-emerald-700">Commercial Terms</Label>
                  <div className="mt-2 space-y-3">
                    {/* Fee Percent */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={terms.feePercent.enabled}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          feePercent: { ...prev.feePercent, enabled: e.target.checked }
                        }))}
                        className="w-4 h-4 flex-shrink-0"
                        data-testid="term-fee-checkbox"
                      />
                      <span className="text-sm w-28 flex-shrink-0">Professional fees</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={terms.feePercent.value}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          feePercent: { ...prev.feePercent, value: e.target.value }
                        }))}
                        className="w-20 h-7 text-sm"
                        data-testid="term-fee-value"
                      />
                      <span className="text-sm">% of annual CTC</span>
                    </div>
                    
                    {/* Replacement Guarantee */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={terms.replacementMonths.enabled}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          replacementMonths: { ...prev.replacementMonths, enabled: e.target.checked }
                        }))}
                        className="w-4 h-4 flex-shrink-0"
                        data-testid="term-replacement-checkbox"
                      />
                      <span className="text-sm w-28 flex-shrink-0">Replacement</span>
                      <Input
                        type="number"
                        value={terms.replacementMonths.value}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          replacementMonths: { ...prev.replacementMonths, value: e.target.value }
                        }))}
                        className="w-20 h-7 text-sm"
                        data-testid="term-replacement-value"
                      />
                      <span className="text-sm">months</span>
                    </div>
                    
                    {/* Payment Days */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={terms.paymentDays.enabled}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          paymentDays: { ...prev.paymentDays, enabled: e.target.checked }
                        }))}
                        className="w-4 h-4 flex-shrink-0"
                        data-testid="term-payment-checkbox"
                      />
                      <span className="text-sm w-28 flex-shrink-0">Payment within</span>
                      <Input
                        type="number"
                        value={terms.paymentDays.value}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          paymentDays: { ...prev.paymentDays, value: e.target.value }
                        }))}
                        className="w-20 h-7 text-sm"
                        data-testid="term-payment-value"
                      />
                      <span className="text-sm">days</span>
                    </div>
                    
                    {/* Min Candidates */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={terms.minCandidates.enabled}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          minCandidates: { ...prev.minCandidates, enabled: e.target.checked }
                        }))}
                        className="w-4 h-4 flex-shrink-0"
                        data-testid="term-candidates-checkbox"
                      />
                      <span className="text-sm w-28 flex-shrink-0">Minimum</span>
                      <Input
                        type="number"
                        value={terms.minCandidates.value}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          minCandidates: { ...prev.minCandidates, value: e.target.value }
                        }))}
                        className="w-12 h-7 text-sm"
                        data-testid="term-candidates-value"
                      />
                      <span className="text-sm">in</span>
                      <Input
                        type="number"
                        value={terms.hireDays.value}
                        onChange={(e) => setTerms(prev => ({
                          ...prev,
                          hireDays: { ...prev.hireDays, value: e.target.value }
                        }))}
                        className="w-14 h-7 text-sm"
                        data-testid="term-days-value"
                      />
                      <span className="text-sm">days</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Review & Send */}
          <div className="lg:col-span-2 space-y-6">
            {/* Review - Step 2 */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">2. Review & Edit</CardTitle>
                  <Button variant="ghost" size="sm" onClick={handleReset}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Email Headers */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>To</Label>
                    <Input
                      type="email"
                      value={toEmail}
                      onChange={(e) => setToEmail(e.target.value)}
                      placeholder="client@company.com"
                      data-testid="to-email-input"
                    />
                  </div>
                  <div>
                    <Label>CC (locked)</Label>
                    <Input
                      value={ccEmail}
                      disabled
                      className="bg-slate-100"
                      data-testid="cc-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    data-testid="subject-input"
                  />
                </div>
                
                {/* Body Editor with Tabs */}
                <div>
                  <Label>Body</Label>
                  <Tabs value={previewTab} onValueChange={setPreviewTab} className="mt-2">
                    <TabsList className="mb-2">
                      <TabsTrigger value="edit">Edit</TabsTrigger>
                      <TabsTrigger value="preview">HTML Preview</TabsTrigger>
                    </TabsList>
                    <TabsContent value="edit">
                      <Textarea
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        className="min-h-[400px] font-mono text-sm"
                        data-testid="body-textarea"
                      />
                    </TabsContent>
                    <TabsContent value="preview">
                      <div 
                        className="border rounded-lg min-h-[400px] overflow-auto bg-white"
                        dangerouslySetInnerHTML={{ __html: generateHtmlPreview() }}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
                
                {/* Send Buttons */}
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={handleSendTest}
                    disabled={sendingTest}
                    data-testid="send-test-btn"
                  >
                    {sendingTest ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Send test to myself
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleSend}
                    disabled={sending || !toEmail}
                    data-testid="send-email-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Email
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Bulk Send Panel */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Bulk Send
                </CardTitle>
                <CardDescription>
                  Send personalized proposals to multiple clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <BulkClientAdder currentValue={bulkRecipients} onAdd={setBulkRecipients} />
                  <Label>Recipients (one per line)</Label>
                  <Textarea
                    value={bulkRecipients}
                    onChange={(e) => setBulkRecipients(e.target.value)}
                    placeholder="Name, email&#10;John Doe, john@company.com&#10;Jane Smith, jane@another.com"
                    className="min-h-[120px] font-mono text-sm"
                    data-testid="bulk-recipients-textarea"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleBulkSend}
                    disabled={sendingBulk || bulkCount === 0}
                    data-testid="bulk-send-btn"
                  >
                    {sendingBulk ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send to {bulkCount} recipient{bulkCount !== 1 ? 's' : ''}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setBulkRecipients('')}
                    disabled={!bulkRecipients}
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>
          
          <TabsContent value="agreement-generator">
            <AgreementGeneratorTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
